import { AxiosInstance } from "axios";

/**
 * Campaign Health's extras (C-16 S8, GLE-8406): the month's goals and pace,
 * the triage log and the launch checklist of a workspace's campaigns, on the
 * role zone. Behind the backend switch `user_roles_campaign_ops_enabled`
 * (manage/me `features.campaign_ops`); off, every route answers 404 + 1035.
 * Admins and the workspace's LGA write; its Pod Leads read (never the goals).
 */

export const CAMPAIGN_TRIAGE_SEVERITIES = {
  INFO: "info",
  WARNING: "warning",
  CRITICAL: "critical",
} as const;

export type CampaignTriageSeverityI =
  (typeof CAMPAIGN_TRIAGE_SEVERITIES)[keyof typeof CAMPAIGN_TRIAGE_SEVERITIES];

/** A campaign's goals for the month, from its campaign group (Pipeline). */
export interface CampaignOpsGoalI {
  group_id: string;
  group_name: string;
  /** The month's QM target as Pipeline's SLA view counts it; null = none. */
  qm_target: number | null;
  bm_goal: number | null;
}

export interface CampaignOpsI {
  campaign_id: string;
  /** Null for a campaign in no group, and always for a Pod Lead. */
  goal: CampaignOpsGoalI | null;
  triage_count: number;
  latest_severity: CampaignTriageSeverityI | null;
  latest_note_at: string | null;
  checklist_done: number;
}

export interface CampaignOpsListI {
  /** The current ET month, "2026-10". */
  month: string;
  business_days: number;
  business_days_elapsed: number;
  checklist_total: number;
  /** Only campaigns with a goal, a note or a ticked item. */
  campaigns: Array<CampaignOpsI>;
}

export interface CampaignTriageNoteI {
  id: string;
  severity: CampaignTriageSeverityI;
  note: string;
  author_user_id: string | null;
  author_name: string | null;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  can_delete: boolean;
}

export interface CampaignTriageNotesPageI {
  notes: Array<CampaignTriageNoteI>;
  next_token: string | null;
}

export const CAMPAIGN_LAUNCH_CHECKLIST_KEYS = [
  "contract_signed",
  "glencoco_instance",
  "screenplay",
  "first_leads",
  "training",
  "launch_showcase",
  "calendars",
  "billing",
  "first_reps_assigned",
  "crm_integrated",
] as const;

export type CampaignLaunchChecklistKeyI =
  (typeof CAMPAIGN_LAUNCH_CHECKLIST_KEYS)[number];

export interface CampaignLaunchChecklistItemI {
  key: CampaignLaunchChecklistKeyI;
  checked: boolean;
  updated_by_name: string | null;
  updated_at: string | null;
}

/** Backend errors 1330-1332. */
export const CAMPAIGN_OPS_ERROR_CODES = {
  BAD_NOTE: 1330,
  NOTE_NOT_FOUND: 1331,
  BAD_CHECKLIST_ITEM: 1332,
} as const;

const workspacePath = (workspaceId: string) =>
  `/v1/manage/workspace/${encodeURIComponent(workspaceId)}`;

const campaignPath = (workspaceId: string, campaignId: string) =>
  `${workspacePath(workspaceId)}/campaign/${encodeURIComponent(campaignId)}`;

export const ManageCampaignOpsAPIEndpoints = (api: AxiosInstance) => ({
  listCampaignOps: (workspaceId: string) =>
    api.get<CampaignOpsListI>(`${workspacePath(workspaceId)}/campaign_ops`),

  listTriageNotes: (
    workspaceId: string,
    campaignId: string,
    nextToken?: string
  ) =>
    api.get<CampaignTriageNotesPageI>(
      `${campaignPath(workspaceId, campaignId)}/triage_notes`,
      { params: nextToken ? { next_token: nextToken } : undefined }
    ),

  createTriageNote: (
    workspaceId: string,
    campaignId: string,
    body: { severity: CampaignTriageSeverityI; note: string }
  ) =>
    api.post<{ note: CampaignTriageNoteI }>(
      `${campaignPath(workspaceId, campaignId)}/triage_notes`,
      body
    ),

  updateTriageNote: (
    workspaceId: string,
    campaignId: string,
    noteId: string,
    body: { severity?: CampaignTriageSeverityI; note?: string }
  ) =>
    api.patch<{ note: CampaignTriageNoteI }>(
      `${campaignPath(workspaceId, campaignId)}/triage_notes/${encodeURIComponent(noteId)}`,
      body
    ),

  deleteTriageNote: (workspaceId: string, campaignId: string, noteId: string) =>
    api.delete(
      `${campaignPath(workspaceId, campaignId)}/triage_notes/${encodeURIComponent(noteId)}`
    ),

  getLaunchChecklist: (workspaceId: string, campaignId: string) =>
    api.get<{ items: Array<CampaignLaunchChecklistItemI> }>(
      `${campaignPath(workspaceId, campaignId)}/launch_checklist`
    ),

  setLaunchChecklistItem: (
    workspaceId: string,
    campaignId: string,
    key: CampaignLaunchChecklistKeyI,
    checked: boolean
  ) =>
    api.put<{ items: Array<CampaignLaunchChecklistItemI> }>(
      `${campaignPath(workspaceId, campaignId)}/launch_checklist/${encodeURIComponent(key)}`,
      { checked }
    ),
});

/**
 * A campaign's block controls in Team → Calendar → Capacity (C-16 S8): its
 * blocks paused or not, and whether blocks may fall on Saturday / Sunday.
 */
export interface CampaignBlockControlsI {
  campaign_id: string;
  paused_at: string | null;
  paused_by_name: string | null;
  pause_note: string | null;
  saturday: boolean;
  sunday: boolean;
}

export interface CampaignBlockControlsResultI {
  campaign: CampaignBlockControlsI;
  /** Future blocks the change cancelled (each caller was notified). */
  cancelled_blocks: number;
}

/** Backend errors 1333 (paused) and 1334 (bad pause or weekend request). */
export const CAMPAIGN_BLOCK_CONTROL_ERROR_CODES = {
  PAUSED: 1333,
  BAD_REQUEST: 1334,
} as const;

export const ManageCampaignBlockControlsAPIEndpoints = (
  api: AxiosInstance
) => ({
  listCampaignBlockControls: (workspaceId: string) =>
    api.get<{ campaigns: Array<CampaignBlockControlsI> }>(
      `${workspacePath(workspaceId)}/campaign_block_controls`
    ),

  setCampaignBlockPause: (
    workspaceId: string,
    campaignId: string,
    body: { paused: boolean; note?: string }
  ) =>
    api.put<CampaignBlockControlsResultI>(
      `${campaignPath(workspaceId, campaignId)}/block_pause`,
      body
    ),

  setCampaignBlockWeekend: (
    workspaceId: string,
    campaignId: string,
    body: { saturday?: boolean; sunday?: boolean }
  ) =>
    api.put<CampaignBlockControlsResultI>(
      `${campaignPath(workspaceId, campaignId)}/block_weekend`,
      body
    ),
});
